import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';

export const Route = createFileRoute('/admin/clientes/')({ component: AdminClientes });

type ClientItem = {
  id: string;
  avatarUrl: string | null;
  city: string | null;
  state: string | null;
  preferredModality: string | null;
  user: { id: string; name: string; email: string; image: string | null; createdAt: string };
  _count: { therapistSessions: number };
};

const MODALITY_LABEL: Record<string, string> = {
  ONLINE: 'Online',
  IN_PERSON: 'Presencial',
  PRESENCIAL: 'Presencial',
  BOTH: 'Online + presencial',
  HYBRID: 'Online + presencial',
  HIBRID: 'Online + presencial',
};

function AdminClientes() {
  const [query, setQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-clients', query],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (query) sp.set('q', query);
      return api.get(`admin/clients?${sp.toString()}`).json<{ items: ClientItem[]; total: number }>();
    },
  });

  const items = data?.items ?? [];

  return (
    <>
      <AdminPageHeader title="Clientes" subtitle={`${data?.total ?? '...'} cadastrados`} />

      <input
        type="search"
        placeholder="Buscar por nome ou e-mail..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="max-w-md w-full bg-white border border-slate-200 rounded-md h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 mb-4"
      />

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-50">
              <tr>
                <Th>Cliente</Th>
                <Th>Cidade / UF</Th>
                <Th>Modalidade</Th>
                <Th align="right">Sessões</Th>
                <Th>Cadastro</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan={5} className="p-12 text-center text-sm text-slate-400">Carregando...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="p-12 text-center text-sm text-slate-400">Nenhum cliente encontrado.</td></tr>
              ) : items.map((c) => {
                const avatar = c.avatarUrl ?? c.user.image;
                return (
                  <tr key={c.id} className="hover:bg-slate-50/60">
                    <td className="px-5 py-3">
                      <Link to="/admin/clientes/$id" params={{ id: c.id }} className="flex items-center gap-3 group">
                        {avatar ? (
                          <img src={avatar} alt="" className="size-9 rounded-full object-cover ring-2 ring-teal-100 shrink-0" />
                        ) : (
                          <div className="size-9 rounded-full bg-gradient-to-br from-teal-300 to-teal-500 flex items-center justify-center text-white font-bold shrink-0">
                            {(c.user.name ?? '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 group-hover:text-teal-600 leading-tight truncate">{c.user.name}</div>
                          <div className="text-[11px] text-slate-400 truncate">{c.user.email}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600">
                      {c.city ? `${c.city}${c.state ? ', ' + c.state : ''}` : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      {c.preferredModality ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-coral-50 text-coral-700 border border-coral-100">
                          {MODALITY_LABEL[c.preferredModality] ?? c.preferredModality}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="font-semibold text-slate-700 tabular-nums">{c._count.therapistSessions}</span>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">
                      {new Date(c.user.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th className={`text-${align} px-5 py-3 text-slate-500 font-semibold uppercase text-xs`}>{children}</th>;
}
